import type { ColumnDef } from '@tanstack/react-table'
import { ShieldCheck } from 'lucide-react'
import { DataTableColumnHeader } from '@/shared/components/data-table'
import { cn } from '@/shared/lib/utils'
import { Avatar, AvatarFallback, AvatarImage } from '@/shared/ui/avatar'
import type { UserAdminRow } from '../models/user-admin'
import { TYPE_ACCOUNT_LABELS } from './users-admin-filter.registry'

export const USERS_ADMIN_COLUMN_NAMES: Record<string, string> = {
  fullname: 'Nombre',
  username: 'Usuario',
  email: 'Correo',
  typeAccount: 'Tipo de cuenta',
  roleNames: 'Roles',
  isTwoFactorAuthenticationEnabled: '2FA',
  active: 'Activo'
}

/**
 * Badge sobrio estilo Shopify: píldora neutra (gris) con un punto de color
 * discreto — el color vive en el punto, no grita en el fondo. Solo los estados
 * que piden atención llevarían fondo de color (acá ninguno).
 */
const TYPE_ACCOUNT_DOTS: Record<string, string> = {
  admin: 'bg-violet-500',
  advisor: 'bg-sky-500',
  user: 'bg-neutral-400'
}

export function TypeAccountBadge({ typeAccount }: { typeAccount: string }) {
  const label = TYPE_ACCOUNT_LABELS[typeAccount]
  if (!label) return <span className="text-sm text-muted-foreground">—</span>
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-foreground/80">
      <span
        aria-hidden="true"
        className={cn('h-1.5 w-1.5 shrink-0 rounded-full', TYPE_ACCOUNT_DOTS[typeAccount])}
      />
      {label}
    </span>
  )
}

/** Iniciales para el fallback del avatar (primer nombre + primer apellido). */
export function userInitials(fullname: string): string {
  const parts = fullname.trim().split(/\s+/)
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase() || '?'
}

export const usersAdminColumns: ColumnDef<UserAdminRow>[] = [
  {
    accessorKey: 'fullname',
    header: ({ column }) => (
      <DataTableColumnHeader className="min-w-[220px]" column={column} name="Nombre" />
    ),
    cell: ({ row }) => (
      <span className="flex items-center gap-2">
        <Avatar seed={row.original._id} className="h-6 w-6">
          {row.original.avatar && <AvatarImage src={row.original.avatar} alt="" />}
          <AvatarFallback className="text-[10px]">
            {userInitials(row.original.fullname)}
          </AvatarFallback>
        </Avatar>
        <span className="truncate font-medium" title={row.original.fullname}>
          {row.original.fullname}
        </span>
      </span>
    )
  },
  {
    accessorKey: 'username',
    header: ({ column }) => <DataTableColumnHeader column={column} name="Usuario" />,
    cell: ({ row }) => <span className="whitespace-nowrap">{row.original.username}</span>
  },
  {
    accessorKey: 'email',
    header: ({ column }) => <DataTableColumnHeader column={column} name="Correo" />,
    cell: ({ row }) => (
      <span className="block max-w-[16rem] truncate" title={row.original.email}>
        {row.original.email}
      </span>
    )
  },
  {
    accessorKey: 'typeAccount',
    header: ({ column }) => <DataTableColumnHeader column={column} name="Tipo de cuenta" />,
    cell: ({ row }) => <TypeAccountBadge typeAccount={row.original.typeAccount} />
  },
  {
    accessorKey: 'roleNames',
    header: ({ column }) => <DataTableColumnHeader column={column} name="Roles" sortable={false} />,
    // Badges sobrios con overflow "+N": legible sin comerse la fila.
    cell: ({ row }) => {
      const roles = row.original.roles
      if (roles.length === 0) return <span className="text-xs text-muted-foreground">—</span>
      const visible = roles.slice(0, 2)
      const hidden = roles.length - visible.length
      return (
        <span className="flex max-w-[20rem] items-center gap-1" title={row.original.roleNames}>
          {visible.map((role) => (
            <span
              key={role._id}
              className="inline-flex max-w-[9rem] items-center truncate rounded-full bg-muted px-2 py-0.5 text-xs text-foreground/70"
            >
              {role.name}
            </span>
          ))}
          {hidden > 0 && (
            <span className="inline-flex items-center rounded-full bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
              +{hidden}
            </span>
          )}
        </span>
      )
    }
  },
  {
    accessorKey: 'isTwoFactorAuthenticationEnabled',
    header: ({ column }) => <DataTableColumnHeader column={column} name="2FA" sortable={false} />,
    cell: ({ row }) =>
      row.original.isTwoFactorAuthenticationEnabled ? (
        <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600">
          <ShieldCheck className="h-3.5 w-3.5" /> Activo
        </span>
      ) : (
        <span className="text-xs text-muted-foreground">—</span>
      )
  },
  {
    accessorKey: 'active',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} name="Activo" sortable={false} />
    ),
    cell: ({ row }) => (
      <span
        className={cn(
          'text-xs font-medium',
          row.original.active ? 'text-emerald-600' : 'text-muted-foreground'
        )}
      >
        {row.original.active ? 'Sí' : 'No'}
      </span>
    )
  }
]
